/**
 * Shared Structured Data (JSON-LD) helpers
 *
 * Canonical schema.org entities and per-page builders, used by BOTH:
 *  - the server-side SEO middleware (initial HTML, read by no-JS AI crawlers
 *    such as GPTBot, PerplexityBot, ClaudeBot, and by Googlebot), and
 *  - the client `useSEO` hook (so structured data is present after in-app SPA
 *    navigation, where the server middleware does not run).
 *
 * FAQ content lives here as the single source of truth so the FAQPage schema
 * always matches the visible on-page copy. Google treats FAQ structured data
 * whose text does not match the page as a structured-data violation, so the
 * rendering components import these same arrays.
 */

import type { FeatureContent } from './featureContent/types';

const BASE_URL = 'https://www.talenture.ai';

export interface FaqEntry {
  question: string;
  answer: string;
}

/** Canonical Organization entity. Referenced on every public page. */
export const ORGANIZATION_SCHEMA = {
  '@type': 'Organization',
  '@id': `${BASE_URL}/#organization`,
  name: 'Talenture.ai',
  url: `${BASE_URL}/`,
  logo: {
    '@type': 'ImageObject',
    url: `${BASE_URL}/talenture-logo.png`,
    width: 512,
    height: 512,
  },
  description:
    'Talenture is an AI layer that works with your ATS, doing the work between job post and first interview: reading and scoring every applicant, confirming deal-breakers, prepping candidates, and booking interviews. Your team makes every decision.',
  sameAs: [
    'https://www.linkedin.com/company/talenture-ai',
    'https://twitter.com/talentureai',
  ],
};

/** Site-level WebSite entity with search action. Used on the homepage. */
export const WEBSITE_SCHEMA = {
  '@type': 'WebSite',
  '@id': `${BASE_URL}/#website`,
  name: 'Talenture.ai',
  url: BASE_URL,
  description:
    'AI recruiting software that works with your ATS. Every applicant read and scored, deal-breakers confirmed, candidates prepped, interviews booked; your team decides.',
  publisher: { '@id': `${BASE_URL}/#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: `${BASE_URL}/jobs?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

/** Product + Offer for the €299 per booked interview pricing model. */
export const PRICING_PRODUCT_SCHEMA = {
  '@type': 'Product',
  '@id': `${BASE_URL}/company/pricing/hiring-app#product`,
  name: 'Talenture.ai Vetted Interview',
  description:
    'An AI-matched, pre-vetted candidate with a first interview already scheduled. Billed once per booked interview. No seats, no subscription, no contract.',
  brand: { '@type': 'Brand', name: 'Talenture.ai' },
  offers: {
    '@type': 'Offer',
    '@id': `${BASE_URL}/company/pricing/hiring-app#offer`,
    price: '299',
    priceCurrency: 'EUR',
    priceValidUntil: '2027-12-31',
    description: 'Per first booked interview. No subscription or per-seat fees.',
    url: `${BASE_URL}/company/pricing/hiring-app`,
    availability: 'https://schema.org/InStock',
    seller: { '@id': `${BASE_URL}/#organization` },
  },
};

/** Product + AggregateOffer for the per-candidate ATS AI-layer pricing model. */
export const ATS_PRICING_PRODUCT_SCHEMA = {
  '@type': 'Product',
  '@id': `${BASE_URL}/company/pricing/ats-layer#product`,
  name: 'Talenture.ai AI Layer for your ATS',
  description:
    'An AI layer on top of your existing ATS. Every candidate profile that flows in is read, scored, pre-vetted, prepped, and scheduled, then handed back to your team as a scorecard. Billed per candidate profile processed.',
  brand: { '@type': 'Brand', name: 'Talenture.ai' },
  offers: {
    '@type': 'AggregateOffer',
    '@id': `${BASE_URL}/company/pricing/ats-layer#offer`,
    priceCurrency: 'EUR',
    lowPrice: '1',
    highPrice: '2.50',
    offerCount: '4',
    priceValidUntil: '2027-12-31',
    description:
      'Prepaid credit packs. One credit processes one candidate profile. Volume discounts from €2.50 down to about €1 per candidate.',
    url: `${BASE_URL}/company/pricing/ats-layer`,
    availability: 'https://schema.org/InStock',
    seller: { '@id': `${BASE_URL}/#organization` },
  },
};

/** FAQ shown on /connect-your-ats. Source of truth for both UI and schema. */
export const CONNECT_ATS_FAQ: FaqEntry[] = [
  {
    question: 'Will this replace our ATS?',
    answer:
      "No. Talenture is an AI workflow layer, not a system of record. Your ATS stays exactly as it is. Talenture plugs in alongside it and handles the steps your ATS doesn't: screening, scheduling, mock interviews, and rejection messages.",
  },
  {
    question: 'How long does setup take?',
    answer:
      "We handle the integration for you. Setup usually takes about a week. You don't need engineering resources on your side.",
  },
  {
    question: 'Which ATS systems do you support?',
    answer:
      "Greenhouse, Lever, Ashby, Workable, Recruitee, Teamtailor, Workday, and SmartRecruiters, with more available on request. Book a call and we'll confirm your setup.",
  },
  {
    question: 'What data passes between systems?',
    answer:
      "Candidate profiles and application status. We use only what's needed to run the workflow, and nothing is shared beyond that.",
  },
  {
    question: 'How is pricing structured?',
    answer:
      'You pay per candidate processed and vetted. Not per seat, not per month. You only spend when Talenture is actively working on your pipeline.',
  },
];

/** FAQ shown on /company/pricing/ats-layer. Source of truth for both UI and schema. */
export const ATS_PRICING_FAQ: FaqEntry[] = [
  {
    question: 'What counts as a billed candidate?',
    answer:
      'One credit is used when a candidate profile flows in from your ATS and Talenture processes it. That candidate gets the full workflow: screening, skills scoring, scheduling, mock interview, interview notes, and follow-ups.',
  },
  {
    question: 'Is there a free trial?',
    answer:
      'Yes. Your first role is on us, up to 50 candidates processed free. Connect your ATS, run a live role through the full workflow, and only buy credits once you have seen the output. We handle the setup and onboard your team personally, live within a week, with no credit card.',
  },
  {
    question: "What if a profile can't be processed?",
    answer:
      "You are never charged for a candidate profile we can't process. You only spend a credit when the AI runs the full workflow on the candidate.",
  },
  {
    question: 'Do credits expire?',
    answer:
      'Prepaid credits are drawn down as candidates are processed. Larger packs lower your price per candidate. Book a call and we will size the right pack for your hiring volume.',
  },
  {
    question: 'How is this different from the €299 vetted interview model?',
    answer:
      'The AI layer keeps your existing ATS and charges a small amount per candidate processed. The vetted interview model runs Talenture as your full hiring app and charges €299 only when an interview is booked. If you already have an ATS, the AI layer is the one to start with.',
  },
];

/** FAQ shown on /company/pricing/hiring-app. Source of truth for both UI and schema. */
export const PRICING_FAQ: FaqEntry[] = [
  {
    question: 'Is this an ATS or a recruiting service?',
    answer:
      "Neither. Talenture is an AI layer that works with the ATS you already have, or runs as your hiring app if you don't have one. It does the work between job post and first interview: reading and scoring every applicant, confirming deal-breakers, prepping candidates, and booking interviews. Nothing is handed outside your team; your recruiters and hiring managers make every decision.",
  },
  {
    question: 'Can we start without changing our process?',
    answer:
      'Yes. Start with one role. Keep your hiring team focused on real conversations.',
  },
  {
    question: 'Is there a subscription or commitment?',
    answer:
      'No. You pay per Vetted Interview, only when a candidate is interview-ready. No subscription, no lock-in.',
  },
  {
    question: 'We already have an ATS. Can we use Talenture alongside it?',
    answer:
      'Yes, that is what the AI layer is built for. Keep your current system of record and add the AI layer on top: it processes every candidate that flows in from your ATS and runs the work between job post and first interview. Billed from €1 per candidate processed. See the Connect Your ATS page for how it works.',
  },
  {
    question: 'What about GDPR and candidate data privacy?',
    answer:
      'All candidate data is GDPR-compliant. Candidates have full control and can request deletion at any time. We never share data with third parties.',
  },
  {
    question: 'Will AI bias affect our candidate pool?',
    answer:
      'Talenture\'s matching is based on skills, experience, and values, not demographic signals. Every match score is explainable: you can see exactly why any candidate ranked where they did.',
  },
];

/** Build a FAQPage node from FAQ entries. */
export function buildFaqPageSchema(entries: FaqEntry[]): object {
  return {
    '@type': 'FAQPage',
    mainEntity: entries.map((entry) => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: { '@type': 'Answer', text: entry.answer },
    })),
  };
}

/**
 * Combine schema.org nodes into a single JSON-LD document via @graph.
 * Each node passed in should omit its own @context.
 */
export function graph(...nodes: object[]): object {
  return {
    '@context': 'https://schema.org',
    '@graph': nodes,
  };
}

function webPageNode(name: string, description: string, urlPath: string): object {
  return {
    '@type': 'WebPage',
    '@id': `${BASE_URL}${urlPath}`,
    name,
    description,
    url: `${BASE_URL}${urlPath}`,
    isPartOf: { '@id': `${BASE_URL}/#website` },
  };
}

/** SoftwareApplication schema representing the Talenture.ai product. */
export const SOFTWARE_APPLICATION_SCHEMA = {
  '@type': 'SoftwareApplication',
  '@id': `${BASE_URL}/#software`,
  name: 'Talenture.ai',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: {
    '@type': 'AggregateOffer',
    lowPrice: '1',
    priceCurrency: 'EUR',
    description: 'Free trial, then per-use pricing from €1 per candidate processed.',
  },
  publisher: { '@id': `${BASE_URL}/#organization` },
  url: `${BASE_URL}/`,
};

export function homePageStructuredData(): object {
  return graph(WEBSITE_SCHEMA, ORGANIZATION_SCHEMA, SOFTWARE_APPLICATION_SCHEMA);
}

export function candidatePageStructuredData(): object {
  return graph(
    webPageNode(
      'For Candidates - Talenture.ai',
      'AI-powered job matching, mock interviews, and free career tools for candidates.',
      '/candidates',
    ),
    ORGANIZATION_SCHEMA,
  );
}

export function connectAtsStructuredData(): object {
  return graph(
    webPageNode(
      'Connect Your ATS - Talenture.ai',
      'Connect your ATS to Talenture.ai for AI-powered candidate scoring and two-way sync.',
      '/connect-your-ats',
    ),
    buildFaqPageSchema(CONNECT_ATS_FAQ),
    ORGANIZATION_SCHEMA,
  );
}

export function pricingHubStructuredData(): object {
  return graph(
    webPageNode(
      'AI Recruiting Software Pricing | Talenture.ai',
      'Two ways to work with Talenture: run it as your full hiring app at €299 per booked interview, or add the AI layer to your existing ATS from about €1 per candidate.',
      '/company/pricing',
    ),
    ORGANIZATION_SCHEMA,
  );
}

export function aboutStructuredData(): object {
  return graph(ORGANIZATION_SCHEMA);
}

export function contactStructuredData(): object {
  return graph(
    {
      '@type': 'ContactPage',
      '@id': `${BASE_URL}/company/contact`,
      name: 'Contact Talenture.ai',
      url: `${BASE_URL}/company/contact`,
      isPartOf: { '@id': `${BASE_URL}/#website` },
    },
    ORGANIZATION_SCHEMA,
  );
}

export function legalPageStructuredData(name: string, description: string, urlPath: string): object {
  return graph(
    webPageNode(name, description, urlPath),
    ORGANIZATION_SCHEMA,
  );
}

export function cvVsMarketStructuredData(): object {
  return graph(
    webPageNode(
      'CV vs Market - Talenture.ai',
      'AI-powered CV analysis showing how candidates compare to real job market requirements.',
      '/cv-vs-market',
    ),
    ORGANIZATION_SCHEMA,
  );
}

/** Build a BreadcrumbList node. The last item should omit `item` per Google's spec. */
export function buildBreadcrumbSchema(
  crumbs: { name: string; item?: string }[],
): object {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      ...(crumb.item ? { item: crumb.item } : {}),
    })),
  };
}

/** Build a HowTo node from ordered steps. */
export function buildHowToSchema(name: string, steps: { title: string; body: string }[]): object {
  return {
    '@type': 'HowTo',
    name,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.title,
      text: step.body,
    })),
  };
}

/**
 * Structured data for a /features/* landing page.
 *
 * Emits WebPage + (optional) HowTo + FAQPage + Organization. The FAQ and HowTo
 * are sourced from the same FeatureContent the page renders, so the schema text
 * always matches the visible copy. FeatureContent is imported type-only to keep
 * this module free of any runtime dependency on the content registry.
 */
export function featureStructuredData(feature: FeatureContent): object {
  const nodes: object[] = [
    webPageNode(feature.seo.title, feature.seo.description, feature.path),
    buildBreadcrumbSchema([
      { name: 'Home', item: `${BASE_URL}/` },
      { name: feature.navLabel },
    ]),
  ];
  if (feature.howTo) {
    nodes.push(buildHowToSchema(feature.howTo.heading, feature.howTo.steps));
  }
  nodes.push(buildFaqPageSchema(feature.faq));
  nodes.push(ORGANIZATION_SCHEMA);
  return graph(...nodes);
}

export function sampleScorecardStructuredData(): object {
  return graph(
    webPageNode(
      'See a Sample Candidate Scorecard | Talenture.ai',
      'The scorecard Talenture produces for every applicant: overall match, eight signals with plain-language reasons, evidence behind each one, and gaps to probe in the interview.',
      '/sample-scorecard',
    ),
    ORGANIZATION_SCHEMA,
  );
}

export function whatYouGetStructuredData(): object {
  return graph(
    webPageNode(
      'What You Get: A Scorecard for Every Candidate | Talenture.ai',
      'For every shortlisted candidate you get a rich profile, a defensible match across 8 signals, an interview playbook, a preparation signal, and a record after the call.',
      '/what-you-get',
    ),
    ORGANIZATION_SCHEMA,
  );
}

/**
 * Canonical mission steps for the /how-it-works page.
 *
 * Single source of truth consumed by both the page component (rendered list)
 * and the HowTo JSON-LD schema, so the two can never diverge.
 */
export const HOW_IT_WORKS_STEPS: { title: string; body: string }[] = [
  {
    title: 'Launch the mission',
    body: 'Launch the mission. Tell Talenture who you need to hire, what matters, and what success looks like.',
  },
  {
    title: 'Build the role',
    body: 'Talenture builds the role. It writes a structured job ad and the criteria, skills, seniority, location, and salary range, that every later step matches against.',
  },
  {
    title: 'Match and qualify every applicant',
    body: 'Talenture matches and qualifies every applicant against the role, with evidence, not keywords.',
  },
  {
    title: 'Prepare candidates before interviews',
    body: 'Candidates are prepared before interviews and any gaps are made clear.',
  },
  {
    title: 'Book interviews into your calendar',
    body: 'Interviews are booked with context, straight into your calendar.',
  },
];

export function howItWorksStructuredData(): object {
  return graph(
    webPageNode(
      'How AI Hiring Works: From Job Post to Interview | Talenture.ai',
      'See how Talenture runs the hiring mission end to end: define the role, match and qualify candidates, prepare them, coordinate interviews, and capture the result.',
      '/how-it-works',
    ),
    buildHowToSchema('How AI Hiring Works: From Open Role to Booked Interview', HOW_IT_WORKS_STEPS),
    ORGANIZATION_SCHEMA,
  );
}

export function pricingStructuredData(): object {
  return graph(
    PRICING_PRODUCT_SCHEMA,
    buildBreadcrumbSchema([
      { name: 'Home', item: `${BASE_URL}/` },
      { name: 'Pricing', item: `${BASE_URL}/company/pricing` },
      { name: 'Hiring App' },
    ]),
    buildFaqPageSchema(PRICING_FAQ),
    ORGANIZATION_SCHEMA,
  );
}

export function atsPricingStructuredData(): object {
  return graph(
    ATS_PRICING_PRODUCT_SCHEMA,
    buildBreadcrumbSchema([
      { name: 'Home', item: `${BASE_URL}/` },
      { name: 'Pricing', item: `${BASE_URL}/company/pricing` },
      { name: 'ATS AI Layer' },
    ]),
    buildFaqPageSchema(ATS_PRICING_FAQ),
    ORGANIZATION_SCHEMA,
  );
}
